import { Link } from "@tanstack/react-router";
import { PauseIcon, PlayIcon } from "lucide-react";
import { AvatarPlaceholder } from "@/components/avatar-placeholder";
import { Container } from "@/components/container";
import { EpisodePlayButton } from "@/components/episode-play-button";
import { FormattedDate } from "@/components/formatted-date";
import { HTML } from "@/components/html";
import { getDownloadUrl } from "@/lib/url";
import type { PublicChannel, PublicEpisode } from "@/orpc/public-schemas";

export function EpisodeEntry({
	episode,
	podcast,
}: {
	episode: PublicEpisode;
	podcast: PublicChannel;
}) {
	return (
		<article aria-labelledby={`episode-${episode.ID}-title`} className="py-10 sm:py-12">
			<Container>
				<div className="flex flex-col items-start">
					<div className="flex w-full items-start gap-4">
						<div className="flex-col">
							<h2
								id={`episode-${episode.ID}-title`}
								className="mt-2 text-lg font-bold text-slate-900"
							>
								<Link
									to={`/$podcast/episodes/$episode`}
									params={{
										podcast: podcast.UrlTitle,
										episode: episode.ID.toString(),
									}}
								>
									{episode.Title}
								</Link>
							</h2>
							<FormattedDate
								date={episode.PublishDate}
								className="order-first font-mono text-sm/7 text-slate-500"
							/>
							<HTML
								className="mt-1 leading-relaxed text-slate-700"
								content={episode.Summary || ""}
							/>
						</div>
						<Link
							to={`/$podcast/episodes/$episode`}
							params={{
								podcast: podcast.UrlTitle,
								episode: episode.ID.toString(),
							}}
							className="relative ml-auto block aspect-square size-12 shrink-0 overflow-hidden rounded-lg bg-slate-200 shadow-xl shadow-slate-200 sm:size-24 sm:rounded"
							aria-label={episode.Title || ""}
						>
							{episode.Icons?.AssetURL ? (
								<img
									className="size-full"
									src={`${import.meta.env.VITE_CDN_URL}${episode.Icons.AssetURL}`}
									alt={episode.Title ?? "Episode cover art"}
								/>
							) : episode.Icons?.ThumbnailURL ? (
								<img
									className="size-full"
									src={`${import.meta.env.VITE_CDN_URL}${episode.Icons.ThumbnailURL}`}
									alt={episode.Title ?? "Episode cover art"}
								/>
							) : podcast.Icon?.AssetURL ? (
								<img
									className="size-full"
									src={`${import.meta.env.VITE_CDN_URL}${podcast.Icon.AssetURL}`}
									alt={episode.Title ?? "Podcast cover art"}
								/>
							) : podcast.Icon?.ThumbnailURL ? (
								<img
									className="size-full"
									src={`${import.meta.env.VITE_CDN_URL}${podcast.Icon.ThumbnailURL}`}
									alt={episode.Title ?? "Podcast cover art"}
								/>
							) : episode.ThemeColor ? (
								<img
									className="size-full"
									src={`${import.meta.env.VITE_CDN_URL}/themeColor/${episode.ThemeColor}-colors.jpg`}
									alt={episode.Title ?? "Podcast cover art"}
								/>
							) : (
								<AvatarPlaceholder theme={episode.ThemeColor as "blue"} className="size-full" />
							)}

							<div className="absolute inset-0 rounded-lg ring-1 ring-black/10 ring-inset sm:rounded" />
						</Link>
					</div>
					<div className="mt-4 flex items-center gap-4">
						<EpisodePlayButton
							episode={{
								id: episode.ID,
								title: episode.Title || "No title",
								description: episode.Description || "",
								summary: episode.Summary || "",
								podcastId: episode.ChannelID,
								published: new Date(episode.PublishDate || new Date()),
								audio: {
									src: getDownloadUrl(
										`/azerion/${podcast.UrlTitle}/${episode.UID.toString().toLowerCase()}.mp3`,
									),
									type: "audio/mpeg",
								},
							}}
							className="flex items-center gap-x-3 text-sm/6 font-medium text-sky-500 hover:text-sky-700 active:text-sky-900"
							playing={
								<>
									<PauseIcon className="h-2.5 w-2.5 fill-current" />
									<span aria-hidden="true">Listen</span>
								</>
							}
							paused={
								<>
									<PlayIcon className="h-2.5 w-2.5 fill-current" />
									<span aria-hidden="true">Listen</span>
								</>
							}
						/>
						<span aria-hidden="true" className="text-sm font-bold text-slate-400">
							/
						</span>
						<Link
							to={`/$podcast/episodes/$episode`}
							params={{
								podcast: podcast.UrlTitle,
								episode: episode.ID.toString(),
							}}
							className="flex items-center text-sm/6 font-medium text-sky-500 hover:text-sky-700 active:text-sky-900"
							aria-label={`Show notes for episode ${episode.Title}`}
						>
							Show notes
						</Link>
					</div>
				</div>
			</Container>
		</article>
	);
}
